import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { toast } from "sonner";
import { Sparkles, Loader2, ArrowRight, HelpCircle, Target, BarChart3, ShieldCheck, AlertTriangle, ClipboardList, Briefcase, FileText } from "lucide-react";
import CreditCostTag from "./CreditCostTag";
import { normalizeGuidedProjectStartResponse } from "./guidedProjectStart";
import { handleAiResponse, unwrapAi } from "../utils/aiResponse";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONFIDENCE_STYLES = {
  high: "border-emerald-500/25 bg-emerald-500/10 text-emerald-300",
  medium: "border-amber-500/25 bg-amber-500/10 text-amber-300",
  low: "border-red-500/25 bg-red-500/10 text-red-300",
};

function AssessmentList({ icon: Icon, title, items, emptyText, tone = "default" }) {
  const iconClassName = tone === "warning" ? "text-amber-300" : "text-primary";
  const bulletClassName = tone === "warning" ? "bg-amber-300/80" : "bg-primary/80";

  return (
    <div className="rounded-lg border border-border/70 bg-background/35 p-3">
      <div className="mb-2 flex items-center gap-2">
        <Icon className={`h-4 w-4 ${iconClassName}`} />
        <h4 className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">{title}</h4>
      </div>
      {items?.length > 0 ? (
        <ul className="space-y-1.5">
          {items.map((item, index) => (
            <li key={`${title}-${index}`} className="flex items-start gap-2 text-sm text-foreground">
              <span className={`mt-2 h-1.5 w-1.5 rounded-full ${bulletClassName}`} aria-hidden="true" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-muted-foreground">{emptyText}</p>
      )}
    </div>
  );
}

function AssessmentChips({ icon: Icon, title, items, emptyText }) {
  return (
    <div className="rounded-lg border border-border/70 bg-background/35 p-3">
      <div className="mb-2 flex items-center gap-2">
        <Icon className="h-4 w-4 text-primary" />
        <h4 className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">{title}</h4>
      </div>
      {items?.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {items.map((item, index) => (
            <span key={`${title}-${index}`} className="rounded-full border border-border/70 bg-background/60 px-2.5 py-1 text-xs font-medium text-foreground">
              {item}
            </span>
          ))}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">{emptyText}</p>
      )}
    </div>
  );
}

function BlackBeltAssessmentPanel({ assessment }) {
  if (!assessment) return null;

  const confidenceClassName = CONFIDENCE_STYLES[assessment.confidence_level] || CONFIDENCE_STYLES.medium;

  return (
    <div className="rounded-xl border border-primary/20 bg-primary/8 p-4 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-primary/15 pb-3">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-primary" />
            <span className="text-sm font-semibold">Black Belt Assessment</span>
          </div>
          <p className="max-w-2xl text-xs leading-5 text-muted-foreground">
            Preliminary Define-phase assessment based on the stated problem. Items below are analytical inferences for project framing, not confirmed baseline or causal findings.
          </p>
        </div>
        <div className={`rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider ${confidenceClassName}`}>
          Assessment Confidence: {assessment.confidence_level || "medium"}
        </div>
      </div>

      <div className="mt-4 space-y-4">
        <div className="rounded-lg border border-border/70 bg-background/45 p-3.5">
          <div className="mb-2 flex items-center gap-2">
            <FileText className="h-4 w-4 text-primary" />
            <h4 className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Refined Problem Statement</h4>
          </div>
          <p className="text-sm leading-6 text-foreground">
            {assessment.refined_problem_statement || assessment.quantified_problem || "Problem framing remains preliminary and requires further Define input."}
          </p>
          <p className="mt-2 text-[11px] leading-5 text-muted-foreground/80">
            Drafted for Define-phase use. Validate baseline, scope boundaries, and financial consequence before gate review.
          </p>
        </div>

        <div className="grid gap-3 md:grid-cols-2">
          <AssessmentChips
            icon={ClipboardList}
            title="Problem Classification"
            items={assessment.problem_classification}
            emptyText="Problem classification remains provisional until the team adds more Define context."
          />
          <AssessmentChips
            icon={Briefcase}
            title="Likely Impact Areas"
            items={assessment.impact_areas}
            emptyText="Impact areas are not yet explicit from the current problem framing."
          />
        </div>

        <div className="grid gap-3 md:grid-cols-2">
          <AssessmentList
            icon={BarChart3}
            title="Initial CTQs to Evaluate"
            items={assessment.initial_ctqs}
            emptyText="No CTQs are evident yet from the current statement. Define outputs, customer requirements, or performance criteria more explicitly."
          />
          <AssessmentList
            icon={AlertTriangle}
            title="Key Gaps Before Define Gate Review"
            items={assessment.critical_gaps}
            emptyText="No immediate Define gaps were inferred from the current framing, but formal review still requires validation."
            tone="warning"
          />
        </div>
      </div>
    </div>
  );
}

export default function GuidedProjectWizard({ open, onOpenChange }) {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  const [problem, setProblem] = useState("");
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [lastQuantifiedProblem, setLastQuantifiedProblem] = useState("");
  const [projectData, setProjectData] = useState({
    title: "",
    description: "",
    industry: "",
    initial_problem: "",
  });

  const handleProblemSubmit = async () => {
    if (loading) return; // Prevent double-fire
    const normalizedProblem = problem.trim();
    if (!normalizedProblem) {
      toast.error("Please describe your problem");
      return;
    }

    if (aiSuggestions && lastQuantifiedProblem === normalizedProblem) {
      setStep(2);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API}/ai/guided-project-start`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ problem_description: normalizedProblem }),
      });

      if (await handleAiResponse(response)) {
        return;
      }
      if (!response.ok) {
        let message = "Failed to get AI guidance";
        try {
          const errorData = await response.json();
          message = errorData?.detail?.message || errorData?.detail || message;
        } catch {
          // Fall back to generic message when error payload is unavailable.
        }
        throw new Error(message);
      }

      const data = normalizeGuidedProjectStartResponse(unwrapAi(await response.json()), normalizedProblem);
      setAiSuggestions(data);
      setLastQuantifiedProblem(normalizedProblem);
      setProjectData({
        title: data.title || normalizedProblem.substring(0, 50),
        description: data.refined_problem_statement || normalizedProblem,
        initial_problem: normalizedProblem,
        industry: projectData.industry || "",
      });
      setStep(2);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to get AI guidance");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProject = async () => {
    if (!projectData.title.trim()) {
      toast.error("Please enter a project title");
      return;
    }

    setCreating(true);
    try {
      const response = await fetch(`${API}/projects`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...projectData,
          ai_suggestions: aiSuggestions || undefined,
        }),
      });

      if (!response.ok) throw new Error("Failed to create project");

      const project = await response.json();
      toast.success("Project created.");
      resetWizard();
      onOpenChange(false);
      navigate(`/project/${project.project_id}/define`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to create project");
    } finally {
      setCreating(false);
    }
  };

  const resetWizard = () => {
    setStep(1);
    setProblem("");
    setAiSuggestions(null);
    setLastQuantifiedProblem("");
    setProjectData({ title: "", description: "", industry: "", initial_problem: "" });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Target className="w-5 h-5 text-primary" />
            Start a New DMAIC Project
          </DialogTitle>
          <DialogDescription>
            Generate an initial Define-phase assessment, then convert it into a working DMAIC project draft.
          </DialogDescription>
        </DialogHeader>

        {/* Step 1: Problem Input */}
        {step === 1 && (
          <div className="space-y-6">
            <div className="text-center py-4">
              <HelpCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-heading text-lg font-semibold mb-2">
                What problem are you trying to solve?
              </h3>
              <p className="text-sm text-muted-foreground">
                Describe your challenge in plain language. Our AI Black Belt will help you quantify it.
              </p>
            </div>

            <div className="space-y-2">
              <Label>Problem Description</Label>
              <Textarea
                value={problem}
                onChange={(e) => setProblem(e.target.value)}
                placeholder="e.g., 'Too many pharmacy claim errors' or 'Customer complaints are increasing' or 'Production line has too much downtime'"
                rows={4}
                data-testid="problem-input"
              />
              <p className="text-xs text-muted-foreground">
                Don't worry about being precise — we'll help you quantify this.
              </p>
            </div>

            <Button
              onClick={handleProblemSubmit}
              disabled={!problem.trim() || loading}
              className="w-full gap-2"
              data-testid="quantify-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" />
                  Let's Quantify That
                  <CreditCostTag cost={2} />
                </>
              )}
            </Button>
          </div>
        )}

        {/* Step 2: AI Suggestions & Project Creation */}
        {step === 2 && aiSuggestions && (
          <div className="space-y-6">
            <BlackBeltAssessmentPanel assessment={aiSuggestions} />

            {/* Project Details Form */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Project Title</Label>
                <Input
                  value={projectData.title}
                  onChange={(e) => setProjectData({ ...projectData, title: e.target.value })}
                  placeholder="Enter project title"
                  data-testid="project-title"
                />
              </div>

              <div className="space-y-2">
                <Label>Problem Statement (Define Draft)</Label>
                <Textarea
                  value={projectData.description}
                  onChange={(e) => setProjectData({ ...projectData, description: e.target.value })}
                  placeholder="Define-phase problem statement draft"
                  rows={3}
                />
                <p className="text-[11px] leading-5 text-muted-foreground">
                  Review and edit this draft before project creation. It should describe the operational problem, current condition, and business consequence without overstating validated facts.
                </p>
              </div>

              <div className="space-y-2">
                <Label>Industry (Optional)</Label>
                <select
                  value={projectData.industry}
                  onChange={(e) => setProjectData({ ...projectData, industry: e.target.value })}
                  className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                >
                  <option value="">Select industry</option>
                  <option value="Healthcare">Healthcare</option>
                  <option value="Manufacturing">Manufacturing</option>
                  <option value="Finance">Finance</option>
                  <option value="Technology">Technology</option>
                  <option value="Retail">Retail</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>

            <div className="flex gap-4">
              <Button variant="outline" onClick={() => setStep(1)} className="flex-1">
                Back
              </Button>
              <Button
                onClick={handleCreateProject}
                disabled={creating}
                className="flex-1 gap-2"
                data-testid="create-project-btn"
              >
                {creating ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    Create Project
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
